/* eslint-disable complexity */
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { SequenceStore } from '@sequence/sequence.store';
import { fadeIn } from '../animations/fade-in';
import { RequestState } from '../shared/models/request-state';
import { RouteParameterStore } from '../shared/services/route-parameter.store';
import { CrewStore } from './crew/crew.store';
import { CrewRequest } from './crew/models/request/crew.request';
import { CrewListView } from './crew/models/view/crew-list.view';
import { MatDialog } from '@angular/material/dialog';
import { SaveAllDialogComponent } from './save-all-dialog/save-all-dialog.component';
import { KcmStore } from './kcm/kcm.store';
import { LegView } from '@sequence/models/view/leg.view';
import { ReleaseStore } from './release/release.store';
import { WeatherStore } from './weather/weather.store';
import { InboundFlightStore } from './inbound-flight/inbound-flight.store';
import { AircraftStore } from './aircraft/aircraft.store';
import { LoadStore } from './load/load.store';
import { StandbyListStore } from './standby-list/standby-list.store';
import { HotelStore } from '@shared/features/hotel/hotel.store';
import { FieldConditionStore } from '@shared/services/field-condition/field-condition.store';
import { ConnectionStatusService } from '@shared/services/connection-status.service';
import { ReleaseView } from './release/models/view/release.view';
import { JumpseatService } from './standby-list/jumpseat.service';
import { ArmedPaxStore } from './armed-pax/armed-pax.store';
import { StationInfoStore } from '@shared/services/station-info/station-info.store';
import { InboundConnectionsService } from './pax-connections/inbound-connections/inbound-connections.service';
import { OutboundConnectionsService } from './pax-connections/outbound-connections/outbound-connections.service';

@Component({
  selector: 'app-flight-details',
  templateUrl: './flight-details.component.html',
  styleUrls: ['./flight-details.component.scss'],
  standalone: false,
  animations: [fadeIn],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlightDetailsComponent implements OnInit, OnDestroy {
  flightState$? = this.sequenceStore.flight$;
  crewState$?: Observable<RequestState<CrewListView>>;
  legView?: LegView;
  flightUrlList$ = this.sequenceStore.flightUrlList$;
  currentFlightUrlIndex$ = this.sequenceStore.currentFlightUrlIndex$;
  connectionStatus$ = this.connectionStatusService.connectionStatus$;
  release$?: Observable<RequestState<ReleaseView>>;
  destroy$ = new Subject<void>();

  constructor(
    private sequenceStore: SequenceStore,
    private crewStore: CrewStore,
    private kcmStore: KcmStore,
    private stationInfoStore: StationInfoStore,
    private releaseStore: ReleaseStore,
    private armedPaxStore: ArmedPaxStore,
    private weatherStore: WeatherStore,
    private inboundFlightStore: InboundFlightStore,
    private aircraftStore: AircraftStore,
    private loadStore: LoadStore,
    private standbyListStore: StandbyListStore,
    private jumpseatService: JumpseatService,
    private routeParamStore: RouteParameterStore,
    private hotelStore: HotelStore,
    private fieldConditionStore: FieldConditionStore,
    private connectionStatusService: ConnectionStatusService,
    private inboundConnectionsService: InboundConnectionsService,
    private outboundConnectionsService: OutboundConnectionsService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    if (this.flightState$) {
      this.flightState$
        .pipe(takeUntil(this.destroy$))
        .subscribe((flightState) => {
          if (flightState.response) {
            this.legView = flightState.response;
          }
        });
    }

    const crewRequest = this.getCrewRequest();

    if (crewRequest) {
      this.crewState$ = this.crewStore.getCrew(crewRequest, false);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  onNextFlightClick(): void {
    this.sequenceStore.nextFlight();
  }

  onPrevFlightClick(): void {
    this.sequenceStore.prevFlight();
  }

  refreshFlight(): void {
    this.flightState$ = this.sequenceStore.refreshFlight();
  }

  /** Create observables to pass to dialog, which will subscribe to them. */
  onSaveAllClicked(): void {
    let crewState$ = null;
    let crewLocationState$ = null;
    let kcmState$ = null;
    let depStationInfoState$ = null;
    let arrStationInfoState$ = null;
    let releaseState$ = null;
    let armedPaxState$ = null;
    let weatherState$ = null;
    let inboundCrewListState$ = null;
    let inboundFlightStatusState$ = null;
    let aircraftDetailsState$ = null;
    let melsState$ = null;
    let aircraftRoutingState$ = null;
    let paxLoadState$ = null;
    let standbyListState$ = null;
    let hotelState$ = null;
    let depFieldReport$ = null;
    let arrFieldReport$ = null;
    let jumpseaters$ = null;
    let jumpseatersIsLoading$ = null;
    let inboundConnectionsState$ = null;
    let outboundConnectionsState$ = null;

    // Get required request info.
    const depAirport =
      this.routeParamStore.currentFlightParams?.depStation || '';

    const arrAirport =
      this.routeParamStore.currentFlightParams?.arrStation || '';

    if (depAirport) {
      depFieldReport$ = this.fieldConditionStore.getFieldCondition(depAirport);
      depStationInfoState$ = this.stationInfoStore.getStationInfo(depAirport);

      if (this.legView?.isDpStart) {
        // Use get vs refresh version since KCM data is rarely changed.
        kcmState$ = this.kcmStore.getKcm(depAirport);
      }
    }

    if (arrAirport) {
      arrFieldReport$ = this.fieldConditionStore.getFieldCondition(arrAirport);
      arrStationInfoState$ = this.stationInfoStore.getStationInfo(arrAirport);
    }

    // Create request observables.
    const releaseRequest = this.legView?.getReleaseRequest();
    if (releaseRequest) {
      releaseState$ = this.releaseStore.getRelease(releaseRequest, true);
    }

    const crewRequest = this.getCrewRequest();
    if (crewRequest) {
      crewState$ = this.crewStore.getCrew(crewRequest, true);
      crewLocationState$ = this.crewStore.getCrewLocation(crewRequest, true);
    }

    if (this.legView) {
      weatherState$ = this.weatherStore.getWeather(this.legView, true);
      inboundCrewListState$ = this.inboundFlightStore.getInboundCrewList(
        this.legView,
        true,
      );
      armedPaxState$ = this.armedPaxStore.getArmedPax(this.legView, true);
      inboundFlightStatusState$ =
        this.inboundFlightStore.getInboundFlightStatus(this.legView, true);
      aircraftDetailsState$ = this.aircraftStore.getAircraftDetails(
        this.legView,
        true,
      );
      melsState$ = this.aircraftStore.getMels(this.legView, true);
      aircraftRoutingState$ = this.aircraftStore.getRouting(this.legView, true);
      paxLoadState$ = this.loadStore.getLoad(this.legView, true);
      standbyListState$ = this.standbyListStore.getFilteredStandbyList(
        this.legView,
        true,
      );
      inboundConnectionsState$ =
        this.inboundConnectionsService.getInboundConnections(
          this.legView,
          true,
        );
      outboundConnectionsState$ =
        this.outboundConnectionsService.getOutboundConnections(
          this.legView,
          true,
        );

      const request = this.legView.getStandbyListRequest();

      if (request) {
        jumpseaters$ = this.jumpseatService.getJumpseatList(request, true);
      }

      jumpseatersIsLoading$ = this.jumpseatService.isLoading$;

      if (this.legView.odlActual.as('minutes') > 0) {
        hotelState$ = this.hotelStore.getHotel(
          this.legView.dutyPeriodIndex,
          true,
        );
      }
    }

    this.dialog.open(SaveAllDialogComponent, {
      data: {
        kcmState$,
        crewState$,
        crewLocationState$,
        releaseState$,
        armedPaxState$,
        weatherState$,
        inboundCrewListState$,
        inboundFlightStatusState$,
        aircraftDetailsState$,
        melsState$,
        aircraftRoutingState$,
        paxLoadState$,
        standbyListState$,
        jumpseaters$,
        jumpseatersIsLoading$,
        hotelState$,
        depFieldReport$,
        arrFieldReport$,
        depStationInfoState$,
        arrStationInfoState$,
        inboundConnectionsState$,
        outboundConnectionsState$,
      },
      disableClose: true,
      maxWidth: '95vw',
      maxHeight: '95vh',
    });
  }

  /** Create crew request from URL parameters. */
  private getCrewRequest(): CrewRequest | null {
    const flightParams = this.routeParamStore.currentFlightParams;

    if (flightParams) {
      const crewRequest: CrewRequest = {
        flightNumber: flightParams?.flightNumber || '',
        date: flightParams?.depDate || '',
        depStation: flightParams?.depStation || '',
      };

      return crewRequest;
    }

    return null;
  }
}
